import { Identifiable, RecordModel } from "../core";
import { components } from "../schema";
import { OwnerType } from "../coreModels";
import { LocationModel } from "./geo";
import { Form, Select } from 'antd';
import { CodepenOutlined, EnterOutlined, HomeOutlined } from "@ant-design/icons";
import { useTheme } from "../../contexts/ThemeProvider";
import AmenityPreview from "../../components/business/AmenityPreview";
import { seoColumn } from "../../components/business/seo/seoColumn";
import { Suspense, lazy } from "react";

// Lazy: both pull in RecordTable and its model graph, and these model files
// are themselves part of that graph.
const DesignLevelsPanel = lazy(() => import("../../components/business/floorPlans/DesignLevelsPanel"));
const UnitLevelsPanel = lazy(() => import("../../components/business/floorPlans/UnitLevelsPanel"));
const DevelopmentListingsPanel = lazy(() => import("../../components/business/developments/DevelopmentListingsPanel"));
const DevelopmentSlidePanel = lazy(() => import("../../components/business/developments/DevelopmentSlidePanel"));

export type IUnit = components['schemas']['Unit'];
export type IUnitType = components['schemas']['Design'];
export type IAmenity = {
  id: number;
  name: string;
  code: string;
  sort_index: number;
  icon: string;
};

export type ICategory = {
  id: number;
  name: string;
}

export type ICategorizedAmenity = {
  id: number;
  category: ICategory;
  amenity: IAmenity;
  note: string;
}

export const UnitTypeModel: RecordModel<IUnitType> = {
  name: "Unit Type",
  path: "housing-designs",
  selectQuery: "&fields=id,name",
  tableQuery: "",
  toString(r): string {
    return r.name ? r.name : r.toString();
  },
  icon: <CodepenOutlined />,
  customTableMappings: {
    ...Object.fromEntries(["galleries", "unit_set"].map(fieldName => ([fieldName, { omit: true }]))),
    "name": { width: 175, render: r => r.name || "awd" },
  },
  // A floor plan belongs to the design: generate once, and every unit of the
  // type inherits it. The per-unit override on UnitModel is the exception.
  floorPlanScope: 'design',
  // Its storeys, once it exists: a new design gets level 0 on save.
  createEndChildren: (record?: IUnitType) => (record?.id ? (
    <Suspense fallback={null}><DesignLevelsPanel designId={Number(record.id)} /></Suspense>
  ) : null),
}

export type IDeveloper = components['schemas']['Developer'];
export type IDevelopment = components['schemas']['Development'];

export const DeveloperModel: RecordModel<IDeveloper> = {
  name: "Developer",
  path: "housing-developers",
  selectQuery: "&fields=id,name",
  tableQuery: "",
  toString: (r) => r.name,
  allowSearch: true,
}

/**
 * A residential project several listings belong to. Its listings are linked
 * from its own drawer; a brochure's development slide is about it.
 */
export const DevelopmentModel: RecordModel<IDevelopment> = {
  name: "Development",
  path: "housing-developments",
  selectQuery: "&fields=id,name",
  tableQuery: "&expand=developer&fields=*,developer.id,developer.name",
  toString: (r) => r.name,
  icon: <CodepenOutlined />,
  allowSearch: true,
  hasImageGalleries: true,
  customTableMappings: {
    ...Object.fromEntries(["galleries", "description"].map(fieldName => ([fieldName, { omit: true }]))),
    name: { width: 220 },
    listings: { render: (r: IDevelopment) => r.listings?.length ?? 0 },
  },
  adjacentModels: [DeveloperModel],
  createEndChildren: (record?: IDevelopment) => (record?.id ? (
    <Suspense fallback={null}>
      <DevelopmentListingsPanel developmentId={Number(record.id)} />
      <div className="mt-lg">
        <h5 className="ant-typography">Brochure slide</h5>
        <DevelopmentSlidePanel developmentId={Number(record.id)} />
      </div>
    </Suspense>
  ) : null),
}

export const UnitModel: RecordModel<IUnit> = {
  name: "Unit",
  path: "housing-units",
  selectQuery: "&fields=id,name",
  // selectQuery: "&fields=id,name,condo.name,base_house_id&expand=condo",
  tableQuery: "",
  toString(r): string {
    return r.name;
  },
  fieldModels: {
    design: 'UnitType'
  },
  hasImageGalleries: true,
  customTableMappings: {
    name: { width: 175 },
    price: { width: 150 },
    ...Object.fromEntries(
      ["design", "lat", "lng", "fractions_taken", "unit_ownerships", "unitopex_set", "unitcapex_set", "zip_code", "address", "usage_pattern", "reserved_fractions"]
        .map(fieldName => ([fieldName, { omit: true }]))
    ),
  },
  customCreateFields: [
    // { name: 'price', component: null }, // we skip setting the price on create
    { name: 'currency_iso', component: () => <Form.Item label="Currency" name="currency_iso"><Select options={[{ value: 'mxn', label: 'MXN' }, { value: 'usd', label: 'USD' }, { value: 'eur', label: 'EUR' }]} /></Form.Item> }, // we skip this as well
    { name: 'reserved_fractions', component: null }
  ],
  icon: <HomeOutlined />,
  adjacentModels: [UnitTypeModel],
  allowSearch: true,
  showsChangeLog: true,
  allowCsvExport: true,
  // An override, for a unit whose layout genuinely differs from its design —
  // mirrored, combined, altered after handover. Most units never need one.
  floorPlanScope: 'unit',
  createEndChildren: (record?: IUnit) => (record?.id ? (
    <Suspense fallback={null}><UnitLevelsPanel unitId={Number(record.id)} /></Suspense>
  ) : null),
}

export interface IListing extends Identifiable {
  name: string;
  slug: string;
  lat: string;
  lng: string;
  type: 'unit' | 'design';
  published: boolean;
  sort_index: number;
  amenities: any[];
  images: string[];
}

export const ListingModel: RecordModel<IListing> = {
  name: "Listing",
  path: "listings",
  selectQuery: "&fields=id,name,slug",
  tableQuery: "&expand=location,unit&fields=*,location.name,location.id,unit.id,unit.name&omit=files,characteristics,design,images",
  toString: (record) => record.name,
  primaryKeyName: 'slug',
  customCreateFields: [
    {
      name: 'status',
      component: (
        <Form.Item name="status" label="Status" rules={[{ required: true }]}>
          <Select placeholder="Select the listing status">
            <Select.Option value="pre_sale">Pre Sale</Select.Option>
            <Select.Option value="new_arrival">New Arrival</Select.Option>
            <Select.Option value="available_now">Available Now</Select.Option>
            <Select.Option value="sold">Sold</Select.Option>
          </Select>
        </Form.Item>
      )
    }
  ],
  customTableMappings: {
    price: { path: ['price', 'amount'] },
    amenities: { omit: true },
    files: { omit: true },
    characteristics: { omit: true },
    design: { omit: true },
    images: { omit: true },
    lat: { omit: true },
    lng: { omit: true },
    address: { omit: true },
    cover_image: { omit: true },
    description: { omit: true },
    usage_modes: { omit: true },
    benefits: { omit: true },
  },
  extraColumns: [
    seoColumn<IListing>({
      pathFor: r => `/listing/${r.slug}`,
      labelFor: r => r.name,
    }),
  ],
  tableFilters: [
    { selectModel: LocationModel },
  ]
}

export const AmenityModel: RecordModel<IAmenity> = {
  name: "Amenity",
  namePlural: "Amenities",
  path: "amenities",
  selectQuery: "",
  tableQuery: "",
  toString(r): string {
    return r.name || r.code;
  },
  toDescriptor: r => (<AmenityPreview amenity={r} />),
  tableFilters: [
    { selectModel: ListingModel }
  ],
  customTableMappings: {
    name: { render: r => <AmenityPreview amenity={r} /> },
    icon: { omit: true }
  },
  useMultipartFormData: true,
  allowSearch: true,
  crossTableQuery: '&fields=id,code,icon',
  crossRecordViews: [
    { toModel: ListingModel, toExtraQuery: '&fields=id,name,amenities' }
  ],
}

export const OwnerModel: RecordModel<OwnerType> = {
  name: "Owner",
  path: "unit-owners",
  selectQuery: "&expand=owner,unit&fields=id,main_accounting_journal,unit.name,owner.full_name,owner.email&has_no_journals=false",
  tableQuery: "&expand=owner,unit",
  toString: (record: OwnerType) => `${record.owner.full_name} - ${record.unit.name}`
}

export const CategoryModel: RecordModel<ICategory> = {
  name: "Category",
  namePlural: "Categories",
  path: 'categories',
  selectQuery: "&expand=amenity,category",
  tableQuery: "&expand=amenity,category",
  toString: r => r.name,
}


export const CategorizedAmenityModel: RecordModel<ICategorizedAmenity> = {
  name: "Categorized Amenity",
  namePlural: "Categorized Amenities",
  path: 'categorized-amenities',
  selectQuery: "&expand=amenity,category",
  tableQuery: "&expand=amenity,category",
  toString: r => `${r.amenity.name} - ${r.category.name}`,
  toDescriptor: r => !!AmenityModel.toDescriptor ? AmenityModel.toDescriptor({ ...r.amenity, name: CategorizedAmenityModel.toString(r) }) : CategorizedAmenityModel.toString(r),
  crossTableQuery: '&expand=amenity,category',
  crossRecordViews: [
    { toModel: ListingModel, toExtraQuery: '&fields=id,name,categorized_amenities' }
  ],
}
